import copy from '@pkg/utils/copy';

const DefaultWhitelist = Object.freeze(new Map([['roles', ['__layer']]]));

function getEntities() {
  return {
    groups: {},
    roles: {},
    activities: {},
    people: {},
  };
}

function getRoot() {
  return {
    created_at: 0,
    name: '',
    objective: '',
    entities: getEntities(),
  };
}

/**
 * @param {Object} src snapshot to copy
 * @param {Boolean} clone values deeply, or reassign otherwise
 * @param {Map} [whitelist] additional properties to retain
 * @returns {Object}
 */
export function make(src, clone = false, whitelist = DefaultWhitelist) {
  const out = getRoot();
  if (!src || typeof src !== 'object') {
    return out;
  }

  out.created_at = src.created_at;
  out.name = src.name;
  out.objective = src.objective;
  out.entities = makeEntities(src.entities, clone, whitelist);

  whitelist.get('snapshot')?.forEach((property) => {
    out[property] = src[property];
  });

  return out;
}

/**
 * @param {Object} src entities to copy
 * @param {Boolean} clone values deeply, or reassign otherwise
 * @param {Map} [whitelist] additional properties to retain
 * @returns {Object}
 */
export function makeEntities(src, clone, whitelist = DefaultWhitelist) {
  const out = getEntities();

  // short-circuit
  if (!src || typeof src !== 'object') {
    return out;
  }

  // groups
  if (src.groups && typeof src.groups === 'object') {
    const hasWhitelist = whitelist.has('groups');
    const propertiesWhitelist = whitelist.get('groups');
    for (const key in src.groups) {
      const group = src.groups[key];
      out.groups[key] = {
        uuid: group.uuid,
        group_uuid: group.group_uuid,
        lead_uuid: group.lead_uuid,
        name: group.name,
        objective: group.objective,
        properties: clone ? copy(group.properties) : group.properties,
        tags: clone ? copy(group.tags) : group.tags,
        order: group.order,
        created_at: group.created_at,
        updated_at: group.updated_at,
      };

      if (hasWhitelist) {
        propertiesWhitelist.forEach((property) => {
          out.groups[key][property] = clone
            ? copy(group[property])
            : group[property];
        });
      }
    }
  }

  // roles
  if (src.roles && typeof src.roles === 'object') {
    const hasWhitelist = whitelist.has('roles');
    const propertiesWhitelist = whitelist.get('roles');
    for (const key in src.roles) {
      const role = src.roles[key];
      out.roles[key] = {
        uuid: role.uuid,
        group_uuid: role.group_uuid ?? null,
        parent_uuid: role.parent_uuid ?? null,
        person_uuid: role.person_uuid ?? null,
        external_id: role.external_id ?? '',
        is_manager: role.is_manager ?? false,
        title: role.title,
        fte: role.fte,
        budget: role.budget,
        properties: clone ? copy(role.properties) : role.properties,
        tags: clone ? copy(role.tags) : role.tags,
        skills: clone ? copy(role.skills) : role.skills,
        order: role.order,
        created_at: role.created_at,
        updated_at: role.updated_at,
      };

      if (hasWhitelist) {
        propertiesWhitelist.forEach((property) => {
          out.roles[key][property] = clone
            ? copy(role[property])
            : role[property];
        });
      }
    }
  }

  // activities
  if (src.activities && typeof src.activities === 'object') {
    const hasWhitelist = whitelist.has('activities');
    const propertiesWhitelist = whitelist.get('activities');
    for (const key in src.activities) {
      const activity = src.activities[key];
      out.activities[key] = {
        uuid: activity.uuid,
        library_uuid: activity.library_uuid,
        owner_uuid: activity.owner_uuid,
        owner_type: activity.owner_type,
        properties: clone ? copy(activity.properties) : activity.properties,
        tags: clone ? copy(activity.tags) : activity.tags,
        hours: activity.hours,
        order: activity.order,
        created_at: activity.created_at,
        updated_at: activity.updated_at,
        disabled_at: activity.disabled_at,
      };

      if (hasWhitelist) {
        propertiesWhitelist.forEach((property) => {
          out.activities[key][property] = clone
            ? copy(activity[property])
            : activity[property];
        });
      }
    }
  }

  // people
  if (src.people && typeof src.people === 'object') {
    const hasWhitelist = whitelist.has('people');
    const propertiesWhitelist = whitelist.get('people');
    for (const key in src.people) {
      const person = src.people[key];
      out.people[key] = {
        uuid: person.uuid,
        first_name: person.first_name,
        last_name: person.last_name,
        avatar: person.avatar,
      };

      if (hasWhitelist) {
        propertiesWhitelist.forEach((property) => {
          out.people[key][property] = clone
            ? copy(person[property])
            : person[property];
        });
      }
    }
  }

  return out;
}

export default make;
